import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Loader, SegmentedControl } from '@mantine/core'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { getUserDayCalendar } from './helper'
import { timeAgo } from '../../helpers/dateUtils'
import { DisplayZone, formatShortDay, formatTime, providerLabel, sourceColor } from './calendarFormat'

interface IProps {
  userId: string
  isProvider: boolean
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const hoursSourceLabel: Record<string, string> = {
  'weekly-schedule': 'from their weekly schedule',
  'general-hours': 'from their general hours (no specific entry for this weekday)',
  'not-set': 'no working hours set — whole day counts',
  'day-not-enabled': 'this weekday is switched off in their schedule',
}

const syncErrorLabel: Record<string, string> = {
  unauthorized: 'access was revoked or expired — user needs to reconnect',
  network: 'network error reaching the calendar provider',
  unknown: 'unknown error',
}

const pad = (n: number) => String(n).padStart(2, '0')
const toDateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ') || '0m'
}

const UserDayCalendar: React.FC<IProps> = ({ userId, isProvider }) => {
  const today = useMemo(() => new Date(), [])
  const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [selected, setSelected] = useState(toDateKey(today))
  const [data, setData] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [displayIn, setDisplayIn] = useState<'user' | 'mine'>('user')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    getUserDayCalendar(userId, isProvider, selected)
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => {
        if (cancelled) return
        setData(undefined)
        setError(err.response?.data?.message || 'Could not load calendar for this user')
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [userId, isProvider, selected])

  const cells = useMemo(() => {
    const first = month.getDay()
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const out: (Date | null)[] = Array(first).fill(null)
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(month.getFullYear(), month.getMonth(), d))
    return out
  }, [month])

  const myZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone
  const userZone: DisplayZone | null = data ? data.timezone : null
  const zone = displayIn === 'user' ? userZone : null

  // The selected date as a label, independent of any timezone math.
  const [sy, sm, sd] = selected.split('-').map(Number)
  const selectedLabel = new Date(sy, sm - 1, sd).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  // Show the day next to times that fall outside the selected day in the
  // display zone (e.g. viewing a US user's evening from Asia).
  const refDay = data ? formatShortDay(data.dayWindowUtc.from, zone) : ''
  const timeWithDay = (iso: string) => {
    const day = formatShortDay(iso, zone)
    return day === refDay ? formatTime(iso, zone) : `${day}, ${formatTime(iso, zone)}`
  }

  const failedCalendars = (data?.connectedCalendars || []).filter((c: any) => c.fetched === false)
  const hours = data?.workingHours

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6">
      <p className="text-xl font-bold">Calendar check</p>
      <p className="text-gray-500 text-sm mb-4">
        Pick a day to see what's on this {isProvider ? 'coach' : 'participant'}'s connected calendar and which hours are
        free, using the same data the booking system uses.
      </p>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Month grid */}
        <div className="w-full lg:w-72 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <button
              aria-label="Previous month"
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            >
              <ChevronLeft size={18} />
            </button>
            <p className="font-bold">{month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            <button
              aria-label="Next month"
              className="p-1 rounded hover:bg-gray-100"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAYS.map((w) => (
              <p key={w} className="text-xs text-gray-500 py-1">
                {w}
              </p>
            ))}
            {cells.map((d, i) => {
              if (!d) return <span key={`empty-${i}`} />
              const key = toDateKey(d)
              const isSelected = key === selected
              const isToday = key === toDateKey(today)
              return (
                <button
                  key={key}
                  onClick={() => setSelected(key)}
                  className={`h-9 rounded-md text-sm ${
                    isSelected
                      ? 'bg-[#0B1D3A] text-white font-bold'
                      : isToday
                      ? 'border border-[#0B1D3A] font-bold hover:bg-gray-100'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {d.getDate()}
                </button>
              )
            })}
          </div>
          <Button
            variant="subtle"
            size="xs"
            className="mt-2"
            onClick={() => {
              setMonth(new Date(today.getFullYear(), today.getMonth(), 1))
              setSelected(toDateKey(today))
            }}
          >
            Today
          </Button>
        </div>

        {/* Day details */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-lg font-bold">{selectedLabel}</p>
            {data && (
              <SegmentedControl
                size="xs"
                value={displayIn}
                onChange={(v) => setDisplayIn(v as 'user' | 'mine')}
                data={[
                  { value: 'user', label: `Their time (${userZone?.zoneName || `UTC${userZone?.utcOffset}`})` },
                  { value: 'mine', label: `My time (${myZoneName})` },
                ]}
              />
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader size="sm" /> Reading their calendar…
            </div>
          )}

          {!loading && error && <Alert color="red">{error}</Alert>}

          {!loading && data && (
            <div className="flex flex-col gap-5">
              {/* Connected calendars */}
              <div>
                <p className="font-bold mb-2">Connected calendar</p>
                {data.connectedCalendars.length === 0 && (
                  <p className="text-gray-600">
                    No calendar connected — only Viveka sessions{isProvider ? ' and working hours' : ''} affect their
                    availability.
                  </p>
                )}
                {data.connectedCalendars.map((c: any) => {
                  const sync = data.syncStatus && data.syncStatus.provider === c.provider ? data.syncStatus : null
                  return (
                    <div key={c.provider} className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge color={sourceColor[c.provider]}>{providerLabel[c.provider]}</Badge>
                      {c.account && <span className="text-gray-700">{c.account}</span>}
                      <span className="text-gray-400 text-sm">· {c.scope}</span>
                      {c.fetched === true && <Badge color="green">Read OK</Badge>}
                      {c.fetched === false && <Badge color="red">Could not read</Badge>}
                      {sync?.status === 'not_synced' && (
                        <span className="text-red-600 text-sm">
                          Not syncing: {syncErrorLabel[sync.lastError] || sync.lastError}
                        </span>
                      )}
                      {sync?.lastSyncedAt && (
                        <span className="text-gray-400 text-sm">last synced {timeAgo(sync.lastSyncedAt)}</span>
                      )}
                    </div>
                  )
                })}
                {failedCalendars.length > 0 && (
                  <Alert color="red" className="mt-2">
                    We couldn't read their {failedCalendars.map((c: any) => providerLabel[c.provider]).join(' / ')}.
                    Events from it are NOT blocking their slots right now, so the list below may be incomplete. Ask them
                    to disconnect and reconnect the calendar.
                  </Alert>
                )}
                {data.calendarFetchFailed && (
                  <Alert color="red" className="mt-2">
                    Calendar lookup failed entirely for this user.
                  </Alert>
                )}
              </div>

              {/* Timezone + hours */}
              <div>
                <p className="font-bold mb-1">Timezone</p>
                <p className="text-gray-700">
                  {data.timezone.zoneName || 'No zone name'} (UTC{data.timezone.utcOffset}) —{' '}
                  {data.timezone.source === 'calendar'
                    ? 'detected from their connected calendar'
                    : data.timezone.source === 'viveka-settings'
                    ? 'from their Viveka availability settings'
                    : 'nothing set, defaulting to UTC'}
                  {data.timezone.source === 'calendar' &&
                    data.timezone.configuredZoneName &&
                    data.timezone.configuredZoneName !== data.timezone.calendarZoneName && (
                      <span className="text-orange-600">
                        {' '}
                        (their Viveka settings say {data.timezone.configuredZoneName}; the calendar's zone wins)
                      </span>
                    )}
                </p>
              </div>

              <div>
                <p className="font-bold mb-1">{data.dayName} working hours</p>
                {hours.fromUtc ? (
                  <p className="text-gray-700">
                    {timeWithDay(hours.fromUtc)} – {timeWithDay(hours.toUtc)}{' '}
                    <span className="text-gray-500 text-sm">({hoursSourceLabel[hours.source]})</span>
                  </p>
                ) : (
                  <p className="text-orange-600">{hoursSourceLabel[hours.source]} — not bookable this day.</p>
                )}
                <p className="text-gray-400 text-xs mt-1">
                  {isProvider
                    ? 'These hours limit when participants can book this coach.'
                    : 'These hours limit when a coach can book this participant (only if they have set availability).'}
                </p>
              </div>

              {/* Events */}
              <div>
                <p className="font-bold mb-2">What's on this day ({data.items.length})</p>
                {data.items.length === 0 && <p className="text-gray-500">Nothing on the calendar this day.</p>}
                <div className="flex flex-col divide-y">
                  {data.items.map((item: any, idx: number) => (
                    <div key={idx} className="flex flex-wrap items-center gap-3 py-2">
                      <span className="font-mono text-sm w-48 shrink-0">
                        {item.allDay ? 'All day' : `${timeWithDay(item.start)} – ${timeWithDay(item.end)}`}
                      </span>
                      <span className="flex-1 min-w-0 break-words">{item.title}</span>
                      <Badge color={sourceColor[item.source]} variant="light">
                        {providerLabel[item.source]}
                      </Badge>
                      {item.markedFree && (
                        <Badge color="orange" variant="light">
                          Marked "free" in calendar — still blocks
                        </Badge>
                      )}
                      {item.cancelled && (
                        <Badge color="orange" variant="light">
                          Cancelled in Outlook — still blocks
                        </Badge>
                      )}
                      {!item.blocksBooking && (
                        <Badge color="gray" variant="light">
                          Info only — doesn't block
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Free time */}
              <div>
                <p className="font-bold mb-2">Free time within working hours</p>
                {hours.fromUtc && data.freeIntervals.length === 0 && (
                  <p className="text-orange-600">
                    No free time left in their working hours this day — that's why no slots show.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {data.freeIntervals.map((f: any, idx: number) => (
                    <span key={idx} className="px-3 py-1 rounded-md text-sm bg-[#DCFCE7] text-[#166534]">
                      {timeWithDay(f.start)} – {timeWithDay(f.end)} ({formatDuration(f.minutes)})
                    </span>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-2">
                  A slot only shows if a gap is at least the session length. When booking, the other person's hours and
                  calendar are also applied, and past times today are hidden.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserDayCalendar
